// apps/web/src/pages/Gallery/AddMusic.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../../../shared/api/axios';
import SmartVideoPlayer from '../Presentation/SmartVideoPlayer';
import { Button } from '../../components/ui/button';
import { useMediaPreview } from '../../../shared/contexts/MediaPreviewContext';
import { useTranslation } from 'react-i18next';
import { getWebFilterStyle,
  type FilterPresetId,
} from '../../../shared/components/filterHelpers.web';

type MusicTrack = {
  _id: string;
  title: string;
  artist: string;
  s3Url?: string;
  url?: string;
  audioUrl?: string;
  duration?: number;
};

type LocState = {
  editTargetId?: string;
  remoteUrl?: string;
  sidebarData?: any;
  videoUrl?: string;
};

type SidebarData = {
  hashtags?: string[];
  title?: string;
  description?: string;
  titleColor?: string;
  descriptionColor?: string;
};

const AddMusic: React.FC = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [musicTracks, setMusicTracks] = useState<MusicTrack[]>([]);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);

  const { mediaItems } = useMediaPreview();

  const srcFileRef = useRef<File | null>(null);
  const createdObjectUrlRef = useRef<string | null>(null);

  const { editTargetId, remoteUrl, sidebarData, videoUrl: legacyVideoUrl } =
    ((location.state as any) || {}) as LocState;

  // 🔎 find the media item so we can read its filterId
  const mediaItem = editTargetId
    ? mediaItems.find((m) => m.id === editTargetId)
    : undefined;

  const filterId: FilterPresetId = (mediaItem?.filterId as FilterPresetId) ?? 'none';

  // Preserve prior sidebar values for "Back"
  const prevSidebar: SidebarData =
    (((location.state as any)?.sidebarData) as SidebarData) ?? {};

  const getApiBaseUrl = () => {
    const env = process.env.REACT_APP_API_BASE_URL;
    return env ? env.replace(/\/api\/?$/, '') : 'http://localhost:3001';
  };

  const isHttp = (u?: string | null) => !!u && /^https?:\/\//i.test(u || '');
  const toHttp = (u?: string | null) =>
    !u ? undefined : isHttp(u) ? u : u!.startsWith('/') ? `${getApiBaseUrl()}${u}` : undefined;

  const toAbsoluteHttp = (u?: string | null) => {
    if (!u) return undefined;
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith('/')) return `${getApiBaseUrl()}${u}`;
    return undefined;
  };

  //Helpers for Filtered Media
  // Heuristic: if the file name was produced by VideoFilter/uploadToR2Direct
const looksBakedName = (name?: string) =>
  !!name && /(_filtered_|_baked)\d*\.mp4$/i.test(name);

const fileSignature = (f?: File | null) =>
  f ? `${f.name}|${f.size}|${(f as any).lastModified || 0}` : 'none';

// What the user picked:
const selectedFilterId: FilterPresetId =
  (mediaItem?.filterId as FilterPresetId) ?? 'none';

// If the file is already baked, don't overlay again in the preview
const isBaked = looksBakedName((mediaItem?.file as File | undefined)?.name);
const effectiveFilterId: FilterPresetId = isBaked ? 'none' : selectedFilterId;

const { cssFilter, overlay } = getWebFilterStyle(effectiveFilterId);


  // Load music list
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/music');
        if (!cancelled) setMusicTracks(res.data || []);
      } catch (err) {
        console.error('Failed to load tracks:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 🔎 Search
  const [query, setQuery] = useState('');
  const filteredTracks = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return musicTracks;
    return musicTracks.filter((t) => {
      const title = (t.title || '').toLowerCase();
      const artist = (t.artist || '').toLowerCase();
      return title.includes(q) || artist.includes(q);
    });
  }, [musicTracks, query]);

  // Resolve source video (prefer File in context)
useEffect(() => {
  const ctxFile = editTargetId
    ? mediaItems.find((m) => m.id === editTargetId)?.file
    : undefined;

  const sig = fileSignature(ctxFile);
  // If the file changed, rebuild object URL
  if (ctxFile instanceof File) {
    srcFileRef.current = ctxFile;

    if (createdObjectUrlRef.current) {
      URL.revokeObjectURL(createdObjectUrlRef.current);
      createdObjectUrlRef.current = null;
    }
    createdObjectUrlRef.current = URL.createObjectURL(ctxFile);
    setVideoPreviewUrl(createdObjectUrlRef.current);
  } else {
    // fall back to HTTP
    const httpFallback = toAbsoluteHttp(remoteUrl) || toAbsoluteHttp(legacyVideoUrl);
    srcFileRef.current = null;
    // Clean up any previous blob URL
    if (createdObjectUrlRef.current) {
      URL.revokeObjectURL(createdObjectUrlRef.current);
      createdObjectUrlRef.current = null;
    }
    setVideoPreviewUrl(httpFallback || null);
  }

  // cleanup on unmount
  return () => {
    if (createdObjectUrlRef.current) {
      URL.revokeObjectURL(createdObjectUrlRef.current);
      createdObjectUrlRef.current = null;
    }
  };
  // depend on the actual file identity + fallbacks
}, [editTargetId, mediaItems, remoteUrl, legacyVideoUrl]);


  // ⏺️ Choose a track
  const handleChoose = (t: MusicTrack) => {
    const ctxFile = editTargetId
      ? mediaItems.find((m) => m.id === editTargetId)?.file
      : undefined;

    const httpFallback =
      toHttp(videoPreviewUrl) || // undefined for blob:
      toHttp(remoteUrl) ||
      toHttp(legacyVideoUrl);

    navigate('/choose-track', {
      state: {
        editTargetId,
        sidebarData,
        videoFile: ctxFile ?? null, // pass File if available
        remoteUrl: ctxFile ? undefined : httpFallback, // HTTP(S) fallback only
        videoUrl: legacyVideoUrl,
        filterId: selectedFilterId,
        track: {
          _id: t._id,
          title: t.title,
          artist: t.artist,
          audioUrl: t.audioUrl || t.url || t.s3Url,
          duration: t.duration,
        },
      },
    });
  };

  return (
    <div
      className="video-editor-layout"
      style={{
        position: 'fixed',
        inset: 0,
        height: '100vh',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateColumns: '420px 1fr',
        gap: 16,
      }}
    >
      {/* ◀︎ Back */}
      <button
        onClick={() =>
          navigate('/gallery-posting', {
            state: {
              reset: false, // keep media
              sidebarData: prevSidebar,
            },
          })
        }
        aria-label={t('gallery.addMusic.back')}
        title={t('gallery.addMusic.back')}
        style={{
          position: 'fixed',
          top: 12,
          left: 12,
          zIndex: 1000,

          width: 38,
          height: 38,
          borderRadius: 9999,
          display: 'grid',
          placeItems: 'center',

          border: '1px solid rgba(255,255,255,0.18)',
          background: 'rgba(0,0,0,0.55)',
          color: '#ccc',
          cursor: 'pointer',
          backdropFilter: 'blur(6px)',
          transition:
            'transform 120ms ease, background 120ms ease, border-color 120ms ease',
        }}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLButtonElement;
          el.style.background = 'rgba(0,0,0,0.7)';
          el.style.borderColor = 'rgba(255,255,255,0.28)';
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLButtonElement;
          el.style.background = 'rgba(0,0,0,0.55)';
          el.style.borderColor = 'rgba(255,255,255,0.18)';
        }}
        onMouseDown={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.96)';
        }}
        onMouseUp={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M15 19L8 12L15 5"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <aside
        className="video-editor-sidebar"
        style={{
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid #222',
          padding: 16,
          minWidth: 0,
          overflow: 'hidden',
          background: '#ccc',
        }}
      >
        <h2 style={{ marginBottom: 12 }}>{t('gallery.addMusic.heading')}</h2>

        {/* 🔎 Search */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            paddingBottom: 10,
            background: '#ccc',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('gallery.addMusic.searchPlaceholder')}
              style={{
                width: '93%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #888',
                background: '#fff',
                color: '#111',
                outline: 'none',
              }}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                title={t('gallery.addMusic.clear')}
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #888',
                  background: '#eee',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Scrollable track list */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 6 }}>
          <ul className="music-track-list" style={{ display: 'grid', gap: 10, margin: 0 }}>
            {filteredTracks.length === 0 && (
              <li style={{ listStyle: 'none', color: '#555', padding: 8 }}>
                {t('gallery.addMusic.noMatches', { query })}
              </li>
            )}

{filteredTracks.map((track) => (
  <li
    key={track._id}
    style={{
      listStyle: 'none',
      border: '1px solid #333',
      borderRadius: 12,
      padding: 10,
      background: '#0f1115',
    }}
  >
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div>
        <div>
          <Link
            to={`/musicdetails/${track._id}`}
            style={{
              fontWeight: 700,
              color: '#ccc',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
            title={t('gallery.addMusic.openDetails', { title: track.title })}
          >
            {track.title}
          </Link>
        </div>
        <div style={{ fontSize: 12, color: '#aaa' }}>{track.artist}</div>
      </div>

      <Button
        onClick={() => handleChoose(track)}
        style={{
          backgroundColor: '#2563eb',
          color: 'white',
          borderRadius: 8,
          padding: '6px 10px',
          height: 34,
          cursor: 'pointer',
          transition: 'filter 120ms ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1.08)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.filter = 'none';
        }}
        title={t('gallery.addMusic.chooseTitle')}
      >
        {t('gallery.addMusic.choose')}
      </Button>
    </div>

    <audio
      src={track.s3Url || track.audioUrl || track.url}
      controls
      style={{ width: '100%', marginTop: 8, background: '#0f1115' }}
    />
  </li>
))}

          </ul>
        </div>
      </aside>

      <main
        className="video-preview-container"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '12px 20px 20px',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {videoPreviewUrl ? (
          // ⬇️ Filtered preview container (same visual look as other screens)
          <div
            style={{
              position: 'relative',
              height: '80vh',
              width: 'auto',
              maxWidth: '95%',
              borderRadius: 12,
              overflow: 'hidden',
              background: '#000',
              // apply CSS filter
              filter: cssFilter ?? 'none',
            }}
          >
            <SmartVideoPlayer
              key={`${videoPreviewUrl}|${filterId}`}
              videoUrl={videoPreviewUrl}
              muted
              controls
              containerStyle={{
                height: '100%',
                width: '100%',
                borderRadius: 0, 
                overflow: 'hidden',
                background: 'transparent',
              }}
              videoStyle={{ height: '100%', width: '100%', objectFit: 'contain' }}
            />

            {/* tint overlay if present */}
            {!!overlay?.tint && (overlay.opacity ?? 0) > 0 && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: overlay.tint,
                  opacity: overlay.opacity,
                  mixBlendMode: filterId === 'noir' ? 'multiply' : 'overlay',
                  pointerEvents: 'none',
                }}
              />
            )}
          </div>
        ) : (
          <p className="text-gray-500">{t('gallery.addMusic.noVideo')}</p>
        )}
      </main>
    </div>
  );
};

export default AddMusic;
